import { useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"

import { authApi, generalApi, userApi } from "../api"
import { useAuth } from "../context/AuthContext"
import { useThemeMode } from "../context/ThemeContext"
import { useNotifications } from "../context/NotificationContext"
import { usePermissions } from "../permissions"
import { SUPPRESSED_PROMPT_HELP } from "../realtime/push"
import { playNotificationSound, soundSupported } from "../realtime/sound"
import PageHeader from "../components/PageHeader"
import UserAvatar, { forgetAvatar } from "../components/UserAvatar"
import PasswordStrength, { MIN_PASSWORD_LENGTH, passwordAccepted } from "../components/PasswordStrength"
import { formatDateTime } from "../utils/format"
import { describeUserAgent } from "../utils/userAgent"
import { MOTION, rise } from "../motion"
import { transition } from "../theme"

/** Four states, four words. "Not asked" used to cover "cannot be asked" as well. */
const PERMISSION_LABELS = {
  granted: "Allowed",
  denied: "Blocked",
  default: "Not asked",
  unsupported: "Unavailable",
}

export default function SettingsPage() {
  const { user, patchUser } = useAuth()
  const [uploading, setUploading] = useState(false)

  const onPickPhoto = async (event) => {
    const file = event.target.files?.[0]
    /* Cleared immediately, so choosing the same file twice in a row still
       fires a change event — otherwise a failed upload cannot be retried
       without picking a different file. */
    event.target.value = ""
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const data = await userApi.uploadAvatar(file)
      /* The old key is dropped from the image cache, or every avatar on screen
         keeps showing the previous photo until a reload. */
      forgetAvatar(user?.avatar_key)
      patchUser({ avatar_key: data.avatar_key })
      setNotice(data.message)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setUploading(false)
    }
  }

  const onRemovePhoto = async () => {
    setUploading(true)
    setError(null)
    try {
      const data = await userApi.removeAvatar()
      forgetAvatar(user?.avatar_key)
      patchUser({ avatar_key: null })
      setNotice(data.message)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setUploading(false)
    }
  }
  const { mode, setThemeMode } = useThemeMode()
  const { permission, enableDesktopNotifications } = useNotifications()
  // What the browser did when we last asked, which is not the same as what it
  // permits — a suppressed request changes neither.
  const [askOutcome, setAskOutcome] = useState(null)
  const { roleLabel, organizationLabel, isBackOffice } = usePermissions()

  const [prefs, setPrefs] = useState({
    notify_in_app: true,
    notify_desktop: true,
    notify_push: true,
    notify_email: false,
    timezone: "UTC",
    date_format: "MM/DD/YYYY",
  })
  const [sessions, setSessions] = useState([])
  const [status, setStatus] = useState(null)

  /* Only once the server has told us. While `status` is still null we do not
     know, and greying out a control on a guess is worse than the row being
     briefly live. */
  const pushUnavailable = Boolean(status) && status.push === "not configured"
  const [notice, setNotice] = useState(null)
  const [error, setError] = useState(null)
  const [passwords, setPasswords] = useState({ current: "", next: "", confirm: "" })
  const passwordsDiffer = passwords.confirm.length > 0 && passwords.next !== passwords.confirm

  useEffect(() => {
    if (!user) return
    setPrefs({
      notify_in_app: Boolean(user.notify_in_app),
      notify_sound: Boolean(user.notify_sound),
      notify_desktop: Boolean(user.notify_desktop),
      notify_push: Boolean(user.notify_push),
      notify_email: Boolean(user.notify_email),
      timezone: user.timezone || "UTC",
      date_format: user.date_format || "MM/DD/YYYY",
    })
  }, [user])

  useEffect(() => {
    authApi
      .sessions()
      .then((data) => setSessions(data.sessions || []))
      .catch(() => setSessions([]))

    generalApi
      .systemStatus()
      .then(setStatus)
      .catch(() => setStatus(null))
  }, [])

  const savePreference = async (key, value) => {
    setPrefs((current) => ({ ...current, [key]: value }))
    try {
      await authApi.savePreferences({ [key]: value })
      patchUser({ [key]: value })
    } catch (err) {
      setError(err.userMessage)
      // Put it back: a switch that stays on after the save failed is a lie about
      // what the server now believes.
      setPrefs((current) => ({ ...current, [key]: !value }))
    }
  }

  const changePassword = async (event) => {
    event.preventDefault()
    setError(null)
    setNotice(null)

    if (passwords.next !== passwords.confirm) {
      setError("The new passwords do not match")
      return
    }
    try {
      const result = await authApi.changePassword(passwords.current, passwords.next)
      setPasswords({ current: "", next: "", confirm: "" })
      setNotice(
        result.other_sessions_revoked > 0
          ? `Password changed. Your other ${result.other_sessions_revoked} session(s) were signed out.`
          : "Password changed.",
      )
      authApi.sessions().then((data) => setSessions(data.sessions || []))
    } catch (err) {
      setError(err.userMessage)
    }
  }

  return (
    <Box>
      <PageHeader title="Settings" subtitle="Your account, how TallyWise looks, and how it reaches you." />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Account
              </Typography>

              {/*
                * A face, which is the one thing on this card that is theirs to
                * choose. Everything under it — name, username, organization —
                * is set by an administrator, so the photo is the part that
                * makes the card worth visiting.
                */}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <UserAvatar name={user?.name} email={user?.email} avatarKey={user?.avatar_key} size={64} tooltip={false} />
                <Box>
                  <Stack direction="row" spacing={1}>
                    <Button component="label" size="small" variant="outlined" color="inherit" disabled={uploading}>
                      {uploading ? "Uploading…" : user?.avatar_key ? "Change photo" : "Add a photo"}
                      <input hidden type="file" accept="image/*" onChange={onPickPhoto} />
                    </Button>
                    {user?.avatar_key ? (
                      <Button size="small" color="inherit" onClick={onRemovePhoto} disabled={uploading}>
                        Remove
                      </Button>
                    ) : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Shown beside your name everywhere in TallyWise.
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={1}>
                <Row label="Name" value={user?.name} />
                {/* The credential, so it is worth being able to look up — an
                    address may be on several accounts and does not identify this
                    one. */}
                <Row label="Username" value={user?.username} />
                <Row label="Email" value={user?.email} />
                <Row label="Organization" value={organizationLabel} />
                <Row label="Role" value={roleLabel || "None assigned"} />
                <Row label="Type" value={isBackOffice ? "Back office" : "Client"} />
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Notifications
              </Typography>

              <Stack spacing={0.5}>
                <FormControlLabel
                  control={<Switch checked={prefs.notify_in_app} onChange={(e) => savePreference("notify_in_app", e.target.checked)} />}
                  label="In-app popups"
                />
                {/* Its own switch rather than part of the popup, because a
                    sound is the one channel that reaches the people around you
                    as well — an open-plan office is a good reason to want the
                    notification and not the noise. */}
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={prefs.notify_sound}
                          disabled={!soundSupported()}
                          onChange={(e) => savePreference("notify_sound", e.target.checked)}
                        />
                      }
                      label="Play a sound"
                    />
                    {/* Hearing it is the only way to decide about it. The
                        button also doubles as the gesture that unlocks audio,
                        which is why it can be pressed while the switch is off. */}
                    <Button
                      size="small"
                      variant="text"
                      disabled={!soundSupported()}
                      onClick={() => playNotificationSound({ refType: "chat_room", force: true })}
                    >
                      Play it
                    </Button>
                  </Stack>
                  {!soundSupported() ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                      This browser has no audio support, so notifications arrive silently.
                    </Typography>
                  ) : null}
                </Box>

                <FormControlLabel
                  control={<Switch checked={prefs.notify_desktop} onChange={(e) => savePreference("notify_desktop", e.target.checked)} />}
                  label="Desktop notifications"
                />
                {/* The one control in the app that is disabled rather than
                    hidden, because the reason is not "you may not" but "this
                    deployment cannot". The caveat used to live at the bottom of
                    the card, two rows below the switch it was about; nobody
                    joined them up, so the switch read as a promise. */}
                <Box sx={{ opacity: pushUnavailable ? 0.55 : 1, transition: transition("opacity", MOTION.base) }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pushUnavailable ? false : prefs.notify_push}
                        disabled={pushUnavailable}
                        onChange={(e) => savePreference("notify_push", e.target.checked)}
                      />
                    }
                    label="Push when TallyWise is closed"
                  />
                  {pushUnavailable ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                      Not available here — background push is not configured on this deployment, so notifications arrive
                      only while TallyWise is open.
                    </Typography>
                  ) : null}
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  Browser permission
                </Typography>
                <Chip
                  size="small"
                  label={PERMISSION_LABELS[permission] || "Not asked"}
                  color={permission === "granted" ? "success" : permission === "denied" ? "error" : "default"}
                />
              </Stack>

              {permission === "default" ? (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1.5 }}
                  onClick={async () => setAskOutcome(await enableDesktopNotifications())}
                >
                  {askOutcome === "suppressed" ? "Ask again" : "Turn on desktop notifications"}
                </Button>
              ) : null}
              {/*
                The button that appears to do nothing.
                Edge holds the request back rather than showing it, and the
                permission stays unset — so without this the chip still reads
                "Not asked" after asking, and the only reasonable conclusion is
                that the button is broken.
              */}
              {permission === "default" && askOutcome === "suppressed" ? (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  <Typography variant="caption">{SUPPRESSED_PROMPT_HELP}</Typography>
                </Alert>
              ) : null}
              {permission === "denied" ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Your browser is blocking notifications for this site. It can only be changed from the browser's own site
                  settings — TallyWise cannot ask again.
                </Typography>
              ) : null}
              {permission === "unsupported" ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  This browser has no desktop notifications at all. Messages and task updates still appear in the app
                  while it is open.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Appearance
              </Typography>
              <TextField select size="small" label="Theme" value={mode} onChange={(e) => setThemeMode(e.target.value)} fullWidth sx={{ mt: 1 }}>
                <MenuItem value="system">Match my system</MenuItem>
                <MenuItem value="light">Light</MenuItem>
                <MenuItem value="dark">Dark</MenuItem>
              </TextField>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Password
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Changing your password signs out your other devices.
              </Typography>
              <form onSubmit={changePassword}>
                <Stack spacing={1.5}>
                  <TextField size="small" type="password" label="Current password" value={passwords.current} onChange={(e) => setPasswords((p) => ({ ...p, current: e.target.value }))} required autoComplete="current-password" />
                  {/* The same meter and the same four checks as the set-password
                      screen. Two components asking for a password two different
                      ways teaches somebody a rule on one screen and contradicts
                      it on the other. */}
                  <Box>
                    <TextField
                      size="small"
                      type="password"
                      label="New password"
                      value={passwords.next}
                      onChange={(e) => setPasswords((p) => ({ ...p, next: e.target.value }))}
                      required
                      fullWidth
                      autoComplete="new-password"
                      error={passwords.next.length > 0 && passwords.next.length < MIN_PASSWORD_LENGTH}
                      helperText={
                        passwords.next.length > 0 && passwords.next.length < MIN_PASSWORD_LENGTH
                          ? `At least ${MIN_PASSWORD_LENGTH} characters — that is ${MIN_PASSWORD_LENGTH - passwords.next.length} more.`
                          : undefined
                      }
                    />
                    <PasswordStrength value={passwords.next} />
                  </Box>
                  <TextField
                    size="small"
                    type="password"
                    label="Confirm new password"
                    value={passwords.confirm}
                    onChange={(e) => setPasswords((p) => ({ ...p, confirm: e.target.value }))}
                    required
                    autoComplete="new-password"
                    error={passwordsDiffer}
                    helperText={passwordsDiffer ? "These do not match" : undefined}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    sx={{ alignSelf: "flex-start" }}
                    disabled={!passwords.current || !passwordAccepted(passwords.next) || passwords.next !== passwords.confirm}
                  >
                    Change password
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="h3" sx={{ flex: 1 }}>
                  Signed-in devices
                </Typography>
                {sessions.length > 1 ? (
                  <Button
                    size="small"
                    onClick={async () => {
                      const result = await authApi.logoutAll().catch(() => null)
                      if (result) {
                        setNotice(`Signed out of ${result.revoked} other device(s).`)
                        authApi.sessions().then((data) => setSessions(data.sessions || []))
                      }
                    }}
                  >
                    Sign out others
                  </Button>
                ) : null}
              </Stack>

              <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
                {sessions.map((session, index) => {
                  const device = describeUserAgent(session.device)
                  return (
                    <Box key={session.id} sx={{ py: 1, ...rise(index) }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                          {device.label}
                        </Typography>
                        {session.is_current ? <Chip size="small" color="primary" label="This device" sx={{ height: 18, fontSize: 10 }} /> : null}
                      </Stack>
                      {/* The full string is still here, one hover away: support
                          asks for it by name often enough that parsing it out of
                          existence would trade one unreadable row for one
                          unanswerable question. */}
                      <Tooltip title={device.raw || "No user-agent recorded"}>
                        <Typography variant="caption" color="text.secondary" sx={{ cursor: "help" }}>
                          {session.ip_address || "—"} · last active {formatDateTime(session.last_active)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  )
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

function Row({ label, value }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ minWidth: 0 }}>
        {value || "—"}
      </Typography>
    </Stack>
  )
}
